import { useEffect, useState } from 'react';
import api from '../../../../../API';
import styles from '../CaseBuilderEnhanced.module.css';

/**
 * Move a case slide back to staging via POST /network/item/restage
 * (clears bdsa linkage and restores original filename).
 */
export default function MoveToStagingModal({ item, open, onClose, onSuccess }) {
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [stagingData, setStagingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !item) return undefined;
        let cancelled = false;
        setError(null);
        setLoading(false);
        setStagingData(null);
        setSelectedFolderId(null);
        api.get('/network/staging/folder').then((res) => {
            if (cancelled || res.status !== 200) return;
            const data = res.data;
            const root = data?.folder;
            if (root?._id) {
                setSelectedFolderId(root._id);
            }
            setStagingData(data);
        }).catch(() => {
            if (!cancelled) setStagingData({ folder: null, folders: [], items: [] });
        });
        return () => { cancelled = true; };
    }, [open, item]);

    if (!open || !item) return null;

    const root = stagingData?.folder;
    const childFolders = stagingData?.folders || [];
    const canConfirm = !!selectedFolderId && !loading;

    const handleConfirm = async () => {
        if (!item?._id || !selectedFolderId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.post('/network/item/restage', {
                query: { itemId: item._id, folderId: selectedFolderId },
            });
            if (res.status === 200) {
                onSuccess?.();
                onClose();
            } else {
                setError(res.data?.message || 'Restage failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to move to staging');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.dialogOverlay} role="dialog" aria-modal="true" aria-labelledby="move-to-staging-title">
            <div className={styles.dialog}>
                <h3 id="move-to-staging-title" className={styles.dialogTitle}>Move to staging</h3>
                <p className={styles.dialogBody}>
                    This slide will leave the case and return to the selected staging folder.
                    Its name reverts to the original filename, and it will no longer appear here.
                </p>
                <p className={styles.dialogBody} style={{ fontWeight: 600, marginTop: '-0.5rem' }}>
                    {item.name || item._id}
                </p>
                <label className={styles.dialogBody} htmlFor="move-to-staging-folder" style={{ display: 'block', marginBottom: '0.35rem' }}>
                    Destination folder
                </label>
                <select
                    id="move-to-staging-folder"
                    value={selectedFolderId || ''}
                    onChange={(e) => setSelectedFolderId(e.target.value || null)}
                    disabled={loading || (!root && childFolders.length === 0)}
                    style={{
                        width: '100%',
                        padding: '0.5rem 0.65rem',
                        marginBottom: '1rem',
                        fontSize: '0.9rem',
                        border: '1px solid #d0d7de',
                        borderRadius: '6px',
                    }}
                >
                    {root && (
                        <option value={root._id}>{root.name} (staging root)</option>
                    )}
                    {childFolders.map((f) => (
                        <option key={f._id} value={f._id}>{f.name}</option>
                    ))}
                    {!root && childFolders.length === 0 && (
                        <option value="">No staging folders available</option>
                    )}
                </select>
                {error && (
                    <p className={styles.dialogBody} style={{ color: '#cf222e' }}>{error}</p>
                )}
                <div className={styles.dialogActions}>
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.buttonDanger}
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                    >
                        {loading ? 'Moving…' : 'Move to staging'}
                    </button>
                </div>
            </div>
        </div>
    );
}
